/**
 * Mobile-only gate — the boot-time device wall, behind the desktop flag.
 *
 * Syntry is a phone/tablet app. When the web build is opened in a desktop
 * browser, `src/main.ts` calls {@link isMobileGateBlocked} BEFORE
 * `createApp(App)` and, on a desktop verdict, mounts the standalone
 * `MobileOnlyPage` root via {@link mountMobileOnlyWall} instead of booting the
 * app. Because the router is never installed on that branch, no route resolves
 * (deep links, `/tabs/*`, the catch-all), no guard runs, and none of the app's
 * side effects start (session restore, token refresher, service worker,
 * version watchdog, survey/install/update prompts). The URL is left untouched
 * so a deep link survives copy-to-phone.
 *
 * **`VITE_DESKTOP_APP_ENABLED`** (phase6-contract.md §1.1, §3.9, DEC-086/087):
 * while the flag is `false` (every shipped build until the readiness doc is
 * signed — docs/desktop-notification-readiness.md) the wall stands and it
 * unregisters any service worker it finds. With the flag `true` the gate is
 * open — {@link isMobileGateBlocked} is `false` before detection runs and the
 * wall never mounts. The flag is a build constant, so a deployment decides
 * once, not per user.
 *
 * Detection is `classifyDevice()` in `src/utils/device.ts` (native shell →
 * UA Client Hints → UA string + touch → interaction media for ambiguous OSes;
 * never viewport width; static per page load).
 *
 * This is a UX gate, NOT a security boundary. UA Client Hints and the UA
 * string are spoofable — auth, tenancy and API access are unchanged either way.
 *
 * QA bypass — **dev builds only** (DEC-086: compiled out of a production
 * build with the flag off, so a shipped build has exactly one desktop switch):
 *   `?mobile_gate=off`  → remembered in localStorage, app boots on desktop
 *   `?mobile_gate=on`   → forgets the override, detection runs again
 *   `localStorage.setItem('syntry.mobile_gate', 'off')` → same as the first
 * Chrome DevTools phone/iPad presets and Firefox RDM device presets classify
 * as mobile by themselves; only DevTools "Responsive" mode with the real UA
 * needs the switch. Verifiers that need the production worker on a desktop use
 * a verification build with `VITE_DESKTOP_APP_ENABLED=true` (§3.12.1).
 */
import { createApp } from 'vue';
import { IonicVue } from '@ionic/vue';
import { createPinia } from 'pinia';
import MobileOnlyPage from '@/views/mobile-only/MobileOnlyPage.vue';
import { classifyDevice } from '@/utils/device';
import { DESKTOP_APP_ENABLED, IS_DEV } from '@/utils/env';
import { useSettingsStore } from '@/stores/settings.store';

/** localStorage key holding the persisted `'off'` override (dotted style like `syntry.onboarding_seen`). */
export const MOBILE_GATE_STORAGE_KEY = 'syntry.mobile_gate';

/** URL query parameter that sets / clears the override. */
export const MOBILE_GATE_QUERY_PARAM = 'mobile_gate';

type GateOverride = 'off' | 'on' | null;

/**
 * Whether the QA bypass exists in this build at all. A production build with
 * the desktop flag off has NO bypass (DEC-086) — the override readers answer
 * `null` without touching the URL or storage.
 */
function overridesAvailable(): boolean {
  return IS_DEV || DESKTOP_APP_ENABLED;
}

function readQueryOverride(): GateOverride {
  if (!overridesAvailable()) return null;
  try {
    const value = new URLSearchParams(window.location.search).get(MOBILE_GATE_QUERY_PARAM);
    if (value === 'off' || value === 'on') return value;
  } catch {
    /* no window / malformed URL — no override */
  }
  return null;
}

function readStoredOverride(): GateOverride {
  if (!overridesAvailable()) return null;
  try {
    return localStorage.getItem(MOBILE_GATE_STORAGE_KEY) === 'off' ? 'off' : null;
  } catch {
    return null; // storage unavailable (private mode) — no bypass
  }
}

function persistOverride(value: GateOverride): void {
  try {
    if (value === 'off') localStorage.setItem(MOBILE_GATE_STORAGE_KEY, 'off');
    else localStorage.removeItem(MOBILE_GATE_STORAGE_KEY);
  } catch {
    /* not persisted; the query override still applies to this load */
  }
}

/**
 * Resolve the QA/dev override. The query parameter wins and is persisted so
 * a developer sets it once per browser profile; the URL itself is never
 * rewritten (no router exists yet, and the param is harmless).
 */
function resolveOverride(): GateOverride {
  const fromQuery = readQueryOverride();
  if (fromQuery === 'off') {
    persistOverride('off');
    return 'off';
  }
  if (fromQuery === 'on') {
    persistOverride(null);
    return 'on';
  }
  return readStoredOverride();
}

/**
 * Should the mobile-only wall replace the app for this page load?
 * `false` outright when the desktop flag is on; otherwise native shells and
 * (dev-only) bypassed browsers never block, and only an explicit "desktop"
 * classification does.
 */
export function isMobileGateBlocked(): boolean {
  if (typeof window === 'undefined') return false;

  if (DESKTOP_APP_ENABLED) {
    console.info('[mobile-gate] open: VITE_DESKTOP_APP_ENABLED');
    return false;
  }

  if (resolveOverride() === 'off') {
    console.info('[mobile-gate] bypassed via override');
    return false;
  }

  const verdict = classifyDevice();
  // Logged in every build: this is what support asks for when a user reports
  // the wall on a real phone.
  console.info(`[mobile-gate] ${verdict.formFactor} (${verdict.reason})`);
  return verdict.formFactor === 'desktop';
}

/**
 * Mount the standalone wall into `#app` in place of the real app.
 *
 * `IonicVue` is required even without an `<ion-app>`: Ionic's structure.css
 * keeps `<body>` hidden until the `ion-ce` class is present, and the plugin
 * also configures `<ion-icon>`. Pinia is installed only so the wall can run
 * the same `initTheme()` that `App.vue` runs — called BEFORE mount so a
 * dark-mode user's first paint is already dark (the app's `onMounted` path
 * never runs on this branch).
 */
export function mountMobileOnlyWall(): void {
  const { reason } = classifyDevice();

  const app = createApp(MobileOnlyPage, { reason })
    .use(IonicVue, { mode: 'ios' })
    .use(createPinia());

  useSettingsStore().initTheme();
  document.title = 'Syntry — Mobile only';

  app.mount('#app');

  // A desktop profile that installed the old PWA should not keep serving a
  // precached copy of the app. Best-effort; phones never reach this branch.
  void unregisterWallServiceWorkers();
}

/**
 * Unregister every service worker on this desktop browser — ONLY while the
 * desktop flag is off (§3.9). With the flag on, a desktop is a supported
 * form factor and its worker is the real app's; this must never touch it.
 * Exported for the mobile-gate spec; the wall is its only runtime caller.
 */
export async function unregisterWallServiceWorkers(): Promise<boolean> {
  if (DESKTOP_APP_ENABLED) return false;
  try {
    const container = typeof navigator !== 'undefined' ? navigator.serviceWorker : undefined;
    if (!container) return false;
    const registrations = await container.getRegistrations();
    await Promise.all(registrations.map((registration) => registration.unregister()));
    return true;
  } catch {
    return false; // unsupported, blocked, or already gone — nothing to do
  }
}
